const toThreshold = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const toEmail = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * Normalize the REKOMENDATOR value of an invoice category into
 * [{ rekomendator: 'a@x.com', value: number | null }, ...] (order preserved).
 * Accepts:
 *  - a JSON array string: '[{"rekomendator":"a@x.com","value":1000}, ...]'
 *  - an already parsed array of { rekomendator, value } (or plain email strings)
 *  - a plain email string (treated as a single approver without limit)
 * Returns [] for empty / invalid input.
 */
export const parseApproverEntries = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string'
          ? { rekomendator: toEmail(item), value: null }
          : { rekomendator: toEmail(item?.rekomendator), value: toThreshold(item?.value) }
      )
      .filter((item) => item.rekomendator);
  }

  if (!value || typeof value !== 'string') {
    return [];
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return [];
  }

  if (trimmedValue.startsWith('[')) {
    try {
      return parseApproverEntries(JSON.parse(trimmedValue));
    } catch (error) {
      console.log(error, 'FAILED TO PARSE REKOMENDATOR');
      return [];
    }
  }

  return [{ rekomendator: trimmedValue, value: null }];
};

/**
 * Select the approvers that apply to an invoice amount (same ladder as the backend):
 *  - approver 1 is always required
 *  - approver N is required only when amount > threshold (`value`) of approver N-1
 *  - `value = null` means no limit: the ladder stops at that approver
 * Example: A (5.000.000), B (null) -> amount <= 5.000.000 : [A]; amount > 5.000.000 : [A, B].
 */
export const filterApproversByAmount = (value, totalAmount) => {
  const candidates = parseApproverEntries(value);

  if (candidates.length === 0) {
    return [];
  }

  const amount = Number(totalAmount) || 0;
  const selected = [candidates[0]];

  for (let index = 1; index < candidates.length; index += 1) {
    const previousThreshold = candidates[index - 1].value;

    if (previousThreshold === null || amount <= previousThreshold) {
      break;
    }

    selected.push(candidates[index]);
  }

  return selected;
};

/**
 * Convert the REKOMENDATOR value of an invoice category into a list of approver emails.
 * When `totalAmount` is provided the list is limited to the approvers required for that
 * amount (see filterApproversByAmount); otherwise every approver is returned.
 * Returns [] for empty / invalid input.
 */
export const parseApproverPayload = (value, totalAmount) => {
  const entries =
    totalAmount === undefined || totalAmount === null
      ? parseApproverEntries(value)
      : filterApproversByAmount(value, totalAmount);

  return entries.map((item) => item.rekomendator);
};
